/***
*
*   POOL ACCESS TOGGLE
*   Editable control for a seat's workspace-pool access (CORE-3864). Used
*   in the account Credits tab's per-seat table. Confirms before applying,
*   updates optimistically, rolls back on failure.
*
**********/

import { useContext, useEffect, useState } from 'react';
import Axios from 'axios';
import { AuthContext, ViewContext } from 'components/lib';
import { buildWebappUrl, recordWebappAudit } from '../../utility/webapp';

const confirmBtn = {
  padding: '8px 16px', fontSize: 13, fontWeight: 600, borderRadius: 6, border: 'none',
  background: '#1974f9', color: '#fff', cursor: 'pointer',
};
const cancelBtn = {
  padding: '8px 16px', fontSize: 13, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
  border: '1px solid #e5e7eb', background: '#fff', color: '#374151',
};

function ConfirmPoolAccessChange({ userLabel, nextEnabled, onCancel, onConfirm }){

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "'Open Sans', sans-serif" }}>
      <p style={{ fontSize: 13, color: '#374151', margin: 0 }}>
        {nextEnabled
          ? `Turn ON workspace pool access for ${userLabel}? They'll be able to spend from the shared pool.`
          : `Turn OFF workspace pool access for ${userLabel}? They'll no longer be able to spend from the shared pool.`}
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" style={cancelBtn} onClick={onCancel}>Cancel</button>
        <button type="button" style={confirmBtn} onClick={onConfirm}>
          {nextEnabled ? 'Enable access' : 'Disable access'}
        </button>
      </div>
    </div>
  );

}

export function PoolAccessToggle({ accountId, userId, userLabel, enabled, onChanged }){

  const authContext = useContext(AuthContext);
  const context = useContext(ViewContext);
  // null = no optimistic override, follow the `enabled` prop
  const [optimistic, setOptimistic] = useState(null);

  // Once the parent refetches and `enabled` catches up with our optimistic
  // value (or a failure's rollback matches it again), drop the override.
  useEffect(() => { setOptimistic(null); }, [enabled]);

  const displayed = optimistic !== null ? optimistic : enabled;

  async function apply(nextEnabled){

    setOptimistic(nextEnabled);

    const url = buildWebappUrl(authContext.remote_server, '/api/credit/admin/pool-access');
    const auditBase = {
      action: 'credit.pool_access_toggled', target_type: 'user', target_id: userId, account_id: accountId,
      method: 'PATCH', endpoint: url,
    };

    try {

      const res = await Axios({ method: 'PATCH', url, data: { accountId, userId, isAccountPoolEnabled: nextEnabled } });
      recordWebappAudit({
        ...auditBase, status: res.status,
        before: { isAccountPoolEnabled: !nextEnabled }, after: { isAccountPoolEnabled: nextEnabled },
      });
      onChanged?.();

    }
    catch (err){

      recordWebappAudit({ ...auditBase, status: err.response?.status ?? 0 });
      setOptimistic(null);
      context.notification.show(err.response?.data?.error || 'There was a glitch in the matrix – please try again', 'error', true);

    }
  }

  function openConfirm(nextEnabled){

    context.modal.show({
      title: 'Change pool access',
      content: (
        <ConfirmPoolAccessChange
          userLabel={userLabel}
          nextEnabled={nextEnabled}
          onCancel={() => context.modal.hide(true)}
          onConfirm={() => { context.modal.hide(true); apply(nextEnabled); }}
        />
      ),
    });

  }

  return (
    <select
      aria-label="Pool access"
      value={displayed ? 'true' : 'false'}
      onChange={(e) => openConfirm(e.target.value === 'true')}
      style={{
        padding: '4px 8px', fontSize: 13, fontWeight: 600, borderRadius: 6,
        border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer',
        color: displayed ? '#16a34a' : '#dc2626', fontFamily: "'Open Sans', sans-serif",
      }}
    >
      <option value="true">true</option>
      <option value="false">false</option>
    </select>
  );

}
